/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {FormHelperText, FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {Text} from '@dropbox/dig-components/typography';
import {Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ImageLine} from '@dropbox/dig-icons/assets';
import {useRef, useState} from 'react';
import {getBackendUrl} from 'utilities';

import type {ImageSlot} from './bannerForm';
import {ACCEPTED_IMAGE_TYPES, imageFileProblem} from './bannerImages';
import styles from './HomeBanners.module.css';

/** Where to show an image slot: a local preview of a picked file, or the stored image. */
export const imageSlotSrc = (slot: ImageSlot) =>
  slot.kind === 'new'
    ? slot.previewUrl
    : slot.kind === 'stored'
      ? `${getBackendUrl()}${slot.path}`
      : undefined;

/**
 * Pick one banner image. The file is checked and previewed in the browser and
 * only uploaded when the form is saved.
 */
export const BannerImagePicker = ({
  id,
  label,
  helperText,
  slot,
  onChange,
}: {
  id: string;
  label: string;
  helperText: string;
  slot: ImageSlot;
  onChange: (slot: ImageSlot) => void;
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  // Only the latest pick counts if an earlier one's check finishes after it.
  const latestPick = useRef(0);
  const src = imageSlotSrc(slot);

  const pick = async (file?: File) => {
    if (!file) {
      return;
    }
    const pickId = ++latestPick.current;
    const previewUrl = URL.createObjectURL(file);
    setIsChecking(true);
    const found = await imageFileProblem(file, previewUrl);
    if (pickId !== latestPick.current) {
      URL.revokeObjectURL(previewUrl);
      return;
    }
    setIsChecking(false);
    setProblem(found);
    if (found) {
      URL.revokeObjectURL(previewUrl);
      return;
    }
    onChange({kind: 'new', file, previewUrl});
  };

  return (
    <FormRow>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <Split gap="12" alignY="center">
        {src && (
          <Split.Item>
            <img src={src} alt={`${label} preview`} className={styles.imageThumb} />
          </Split.Item>
        )}
        <Split.Item>
          <Split gap="8">
            <Button
              id={id}
              variant="opacity"
              size="small"
              isLoading={isChecking}
              withIconStart={<UIIcon src={ImageLine} />}
              aria-describedby={`${id}-help`}
              onClick={() => fileInput.current?.click()}
            >
              {src ? 'Replace' : 'Choose image'}
            </Button>
            {src && (
              <Button
                variant="outline"
                size="small"
                aria-label={`Remove ${label.toLowerCase()}`}
                onClick={() => {
                  setProblem(null);
                  onChange({kind: 'none'});
                }}
              >
                Remove
              </Button>
            )}
          </Split>
        </Split.Item>
      </Split>
      <input
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        ref={fileInput}
        className={styles.hiddenInput}
        data-testid={`${id}-file`}
        onChange={(e) => {
          pick(e.target.files?.[0]);
          // Clear so picking the same file again still fires onChange.
          if (fileInput.current) {
            fileInput.current.value = '';
          }
        }}
      />
      {problem ? (
        <Text size="small" color="error" role="alert">
          {problem}
        </Text>
      ) : (
        <FormHelperText id={`${id}-help`}>{helperText}</FormHelperText>
      )}
    </FormRow>
  );
};
